import React, { useState } from 'react';
import { Button, ButtonGroup, Grid2, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useGetTicketOptions, reorderTicketOptions } from '@openapi/ticket-options/ticket-options';
import { useGetShow } from '@openapi/shows/shows';
import useNotify from '../../hooks/useNotify';
import useSorting from '../../hooks/useSorting';
import { TABLE_ROW_COUNT_DEFAULT } from '../../helpers/constants';
import PageWithTitle from '../../components/atom/PageWithTitle';
import TicketOptionsTable from './components/TicketOptionsTable';
import SearchInput from '../../components/atom/SearchInput';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCapabilities } from '../../hooks/useCapabilities';

const TicketOptionsPage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { notifyError, notifySuccess } = useNotify();
    const { can, CAPABILITIES } = useCapabilities();
    const canManageShows = can(CAPABILITIES.SHOWS_MANAGE);
    const { showId } = useParams();
    const [{ limit, page }, setParams] = useState({ limit: TABLE_ROW_COUNT_DEFAULT, page: 0 });
    const { sort, sortDir, setSorting } = useSorting(['priority', 'ASC']);
    const [search, setSearch] = useState('');
    const [reorderMode, setReorderMode] = useState(false);
    const [pendingOrder, setPendingOrder] = useState(null);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();
    const {
        data,
        isLoading: loading,
        _error,
        refetch: reloadData,
    } = useGetTicketOptions(
        {
            optionalId: showId,
            search,
            page,
            pageSize: limit,
            sort,
            sortDir,
        },
        {
            query: {
                onError: notifyError,
            },
        },
    );

    const {
        data: show,
        isLoading: loadingShow,
        error: _errorShow,
    } = useGetShow(Number(showId), {
        query: {
            onError: notifyError,
        },
    });

    const handleToggleReorder = () => {
        if (!reorderMode) {
            setSorting('priority', 'ASC');
            setSearch('');
            setParams((p) => ({ ...p, page: 0 }));
        }
        setPendingOrder(null);
        setReorderMode((v) => !v);
    };

    const handleSaveOrder = async () => {
        if (!pendingOrder) return;
        setSaving(true);
        try {
            await reorderTicketOptions({ showId: Number(showId), items: pendingOrder });
            notifySuccess(t('ticketoptions.notifications.reorderSaved'));
            setPendingOrder(null);
            setReorderMode(false);
            reloadData();
        } catch (_err) {
            notifyError(t('ticketoptions.notifications.reorderError'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <PageWithTitle
            title={`${t('ticketoptions.title')} (${loadingShow ? '' : show.title || ''})`}
            stackHeadline
            headline={[
                <Grid2
                    container
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    wrap="nowrap"
                    key="search-and-buttons"
                >
                    {!isMobile && (
                        <Grid2 sx={{ mr: 2 }}>
                            <SearchInput
                                search={search}
                                setSearch={setSearch}
                                setParams={setParams}
                                limit={limit}
                            />
                        </Grid2>
                    )}
                    <Grid2 sx={{ flexShrink: 0 }}>
                        {canManageShows && (
                            <ButtonGroup variant="contained">
                                <Tooltip title={t('ticketoptions.reorderMode')}>
                                    <Button
                                        fullWidth
                                        color={reorderMode ? 'primary' : 'secondary'}
                                        onClick={handleToggleReorder}
                                        disabled={saving}
                                    >
                                        <DragIndicatorIcon />
                                    </Button>
                                </Tooltip>
                                {reorderMode && pendingOrder && (
                                    <Tooltip title={t('common.save')}>
                                        <Button
                                            fullWidth
                                            color="success"
                                            onClick={handleSaveOrder}
                                            disabled={saving}
                                        >
                                            <SaveIcon />
                                        </Button>
                                    </Tooltip>
                                )}
                                <Tooltip title={t('ticketoptions.newTicketOptionName')}>
                                    <Button
                                        fullWidth
                                        onClick={() => {
                                            navigate(`/shows/${showId}/ticketoptions/new`);
                                        }}
                                    >
                                        <AddIcon />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        )}
                    </Grid2>
                </Grid2>,
            ]}
        >
            {isMobile && (
                <Grid2 size={{ xs: 12 }}>
                    <SearchInput
                        search={search}
                        setSearch={setSearch}
                        setParams={setParams}
                        limit={limit}
                        fullWidth
                    />
                </Grid2>
            )}
            <TicketOptionsTable
                rows={data?.ticketOptions}
                limit={limit}
                page={page}
                setParams={setParams}
                total={data?.total}
                loading={loading}
                reloadData={reloadData}
                sort={sort}
                sortDir={sortDir}
                setSorting={setSorting}
                showId={showId}
                reorderMode={reorderMode}
                onOrderChange={(items) =>
                    setPendingOrder(
                        items.map((item) => ({ ...item, priority: item.priority + page * limit })),
                    )
                }
            />
        </PageWithTitle>
    );
};

export default TicketOptionsPage;
